import { SlashCommand } from '@@types/Api/Command';
import { CHANNEL_ID } from '@@types/common';
import Member from '@@types/Member';

import { EDITOR_ID } from '~/constants';
import { getEmojiList } from '~/emojiSet';
import { Hint } from '~/hints/Hint';
import { isArchivedChannel, isMeChannel, isPrivateChannel } from '~/services/channelService';
import { channelsStore } from '~/stores/ChannelsStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { editorHintStore } from '~/stores/EditorHintStore';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import logging from '~/stores/providers/logging';
import { slashCommandAppsStore } from '~/stores/SlashCommandAppsStore';
import { threadChannelLogsStore } from '~/stores/ThreadChannelLogsStore';
import { userStore } from '~/stores/UserStore';
import { getCursor, replaceRange } from '~/utils/editorUtil';
import {
  includesInsensitive,
  includesInsensitiveNonCurry,
  isNonEmpty,
  startsWithInsensitive,
  wrapLoading,
} from '~/utils/fnUtil/fnUtil';
import CodeMirror, { Editor } from 'codemirror';
import { debounce, isEmpty } from 'lodash';
import { action, comparer, computed, when as mWhen } from 'mobx';
import {
  __,
  concat,
  cond,
  curry,
  equals,
  evolve,
  F,
  filter,
  ifElse,
  includes,
  map,
  pipe,
  propEq,
  propOr,
  propSatisfies,
  reject,
  subtract,
} from 'ramda';

import Actions from '../Actions';
import { stores } from '../Stores';

type AppRow = Record<string, unknown>;

class HintActions {
  private filterIncludeProp = curry(<T>(value: string, prop: string, objs: T[]) => {
    return filter(propSatisfies(includesInsensitive(value), prop), objs);
  });

  constructor(private actions: Actions) {}

  @computed
  private get isMainChannelEditor() {
    return editorHintStore.editorId === EDITOR_ID.MAIN || editorHintStore.editorId === EDITOR_ID.LOG;
  }

  @computed
  private get mainContentsChannelId() {
    if (this.isMainChannelEditor) {
      return mainDetailUiState.contentsChannelId;
    }

    return threadChannelLogsStore.threadPanelParentLog?.channelId;
  }

  @computed
  private get contentsChannelId() {
    if (this.isMainChannelEditor) {
      return mainDetailUiState.contentsChannelId;
    }
    return threadDetailUiState.contentsChannelId;
  }

  private get mainContentsChannel() {
    return channelsStore.getById(this.mainContentsChannelId);
  }

  public getHintEditor() {
    return this.actions.editorActions.getEditorById(editorHintStore.editorId);
  }

  @computed
  private get activeHintItem() {
    return editorHintStore.activeItem;
  }

  @computed
  private get currentChannelCommands() {
    if (editorHintStore.editorId === EDITOR_ID.THREAD_MAIN) {
      const meetingAppIds = [userStore.meetingId, userStore.webexId];
      return pipe<any, any, any, any>(
        propOr(propOr([], this.mainContentsChannelId)(slashCommandAppsStore.channelCommands), this.contentsChannelId),
        filter((command: SlashCommand) => meetingAppIds.includes(command.integrationAppId)),
        map((command: SlashCommand) => this.createCommandRow('normal', command))
      )(slashCommandAppsStore.channelCommands);
    }
    return pipe(
      propOr([], this.contentsChannelId),
      map((command: SlashCommand) => this.createCommandRow('normal', command)),
      this.appendDoorayAppRows
    )(slashCommandAppsStore.channelCommands);
  }

  @computed
  private get channelMention() {
    return this.makeChannelMention(this.contentsChannelId || this.mainContentsChannelId);
  }

  private makeChannelMention = (channelId: CHANNEL_ID) => {
    return {
      id: channelId,
      defaultOrganizationId: userStore.orgId,
      name: 'Channel',
      tenantMemberRole: 'channel',
    };
  };

  @computed
  private get defaultChannelMentionItems(): Member[] {
    const contentsChannel = this.mainContentsChannel;
    if (isEmpty(contentsChannel)) {
      return [];
    }
    const members = this.mainContentsChannel.members.map(interTenantMembersStore.getById);
    return isPrivateChannel(contentsChannel) ? [this.channelMention, ...members] : members;
  }

  public completeHint = (editor: Editor, item) => {
    if (!editor) {
      return;
    }
    const { hint, hintQuery } = editorHintStore;
    const itemText = hint.convertItemToText(item);
    const queryLength = hintQuery.length;

    const cursor = getCursor(editor);
    const range = {
      from: evolve({ ch: subtract(__, queryLength + hint.fromDistance) }, cursor),
      to: cursor,
    };
    replaceRange(editor, itemText, range.from, range.to, hint.origin);
    this.actions.editorActions.focusEditor(editor);
    this.clearEditorHint();
  };

  public clearEditorHint = () => {
    this.actions.observeActions.hintQuery?.();
    editorHintStore.clearEditorHint();
  };

  public findAndSetHint = ({
    hints,
    text,
    cursor,
    editorId,
  }: {
    hints: Hint[];
    text: string;
    cursor: CodeMirror.Position;
    editorId: EDITOR_ID;
  }) => {
    const hint = this.findHint(hints, text, cursor);
    if (!hint) {
      this.clearEditorHint();
      return;
    }
    this.handleFoundHint(editorId, hint, text);
  };

  public handleHintUp = () => {
    if (!this.activeHintItem) return CodeMirror.Pass;
    editorHintStore.decreaseActiveIndex();
  };

  public handleHintDown = () => {
    if (!this.activeHintItem) return CodeMirror.Pass;
    editorHintStore.increaseActiveIndex();
  };

  public handleHintEnter = (editor: Editor) => {
    const isFetchingItems = editorHintStore.isFetchingItems;
    if (!isFetchingItems && !this.activeHintItem) return CodeMirror.Pass;
    if (isFetchingItems) {
      mWhen(
        () => !isFetchingItems,
        () => this.handleCurrentItem(editor, this.activeHintItem)
      );
    } else {
      this.handleCurrentItem(editor, this.activeHintItem);
    }
  };

  public handleHintEsc = () => {
    this.clearEditorHint();
  };

  public getHintAndQuery = () => {
    return {
      hintName: editorHintStore.hint?.name,
      query: editorHintStore.hintQuery,
    };
  };

  private createCommandRow = (subType: 'normal' | 'system', command: any) => {
    return {
      type: 'command',
      id: command.id,
      command,
      subType,
    };
  };

  private filterEmojisByQuery = (query: string) => {
    const list = [];
    for (let item of getEmojiList()) {
      if (startsWithInsensitive(query, item.value)) {
        list.push(item);
      }
      if (list.length === 20) {
        return list;
      }
    }
    return list;
  };

  private findHint = (hints: Hint[], text: string, cursor: CodeMirror.Position) => {
    const prevHint = editorHintStore.hint;
    if (prevHint?.checkActive(text, cursor)) {
      return prevHint;
    }
    return hints.find((hint) => hint.checkActive(text, cursor));
  };

  private handleFoundHint = (editorId: EDITOR_ID, hint: Hint, text: string) => {
    const prevHint = editorHintStore.hint;
    if (prevHint?.name === hint.name) {
      editorHintStore.setText(text);
    } else {
      editorHintStore.setEditorHint({ editorId, hint, text });
      this.actions.observeActions.observeHintQuery();
    }
  };

  private concatDefaultMentionItems = (query, items: Member[]): Member[] => {
    const myTenantId = stores.user.tenantId;
    const channelMembers = this.defaultChannelMentionItems.filter((item) => {
      return (
        item &&
        (item.tenantMemberRole === 'channel' || item.tenantId !== myTenantId) &&
        includesInsensitiveNonCurry(query, item.name)
      );
    });
    return concat(channelMembers, items);
  };

  private handleCurrentItem = (editor: Editor, item) => {
    if (isEmpty(item)) return;
    this.completeHint(editor, item);
  };

  private searchMentionHintQuery = (query: string) => {
    if (query) {
      this.searchAndSetMentionItems(query);
    } else {
      this.setDefaultChannelMentionItems();
    }
  };

  private searchEmojiHintQuery = (query: string) => {
    this.setItems(this.filterEmojisByQuery(query));
  };

  private searchCommandHintQuery = (query: string) => {
    editorHintStore.setCommands(this.filterCommands(query, this.currentChannelCommands));
  };

  private canUseMeeting = (row) => {
    return (
      (this.mainContentsChannel && !isMeChannel(this.mainContentsChannel)) ||
      (userStore.meetingId && row.command.integrationAppId !== userStore.meetingId)
    );
  };

  private filterCommands = (query, rows) => {
    return rows.filter((row) => includesInsensitiveNonCurry(query, row.command.name) && this.canUseMeeting(row));
  };

  private appendDoorayAppRows = (normalAppRows: AppRow[]) => {
    const commandRows = stores.doorayAppCommands.map((command) => this.createCommandRow('system', command));
    return [...normalAppRows, ...commandRows];
  };

  private searchCommandMentionHintQuery = (query: string) => {
    ifElse(isNonEmpty, this.searchAndSetCommandMentionItems, this.setEmptyItems)(query);
  };

  private getMemberRejector = () => {
    if (editorHintStore.text.startsWith('/invite ')) {
      return (member) => includes(member.id, this.mainContentsChannel?.members ?? []);
    } else {
      return F;
    }
  };

  private searchAndSetCommandMentionItems = (query: string) => {
    wrapLoading(
      () =>
        this.actions.searchActions
          .searchMembers({ keyword: query, boostMembersId: this.mainContentsChannel?.members ?? [] })
          .then(reject(this.getMemberRejector()))
          .then(this.setItems, logging.error),
      this.setFetchingItems
    );
  };

  private setEmptyItems = () => {
    this.setItems([]);
  };

  @computed({ equals: comparer.shallow })
  private get nonArchivedTopicChannels() {
    return channelsStore.topicChannels.filter((channel) => !isArchivedChannel(channel));
  }

  private searchCommandChMentionHintQuery = (query: string) => {
    pipe<any, any, any, any>(
      reject(propEq('channelId', this.contentsChannelId)),
      this.filterIncludeProp(query, 'title'),
      this.setItems
    )(this.nonArchivedTopicChannels);
  };

  @action
  private handleQuery = (query: string, type: string) => {
    cond<[string], void>([
      [equals('mention'), () => this.searchMentionHintQuery(query)],
      [equals('emoji'), () => this.searchEmojiHintQuery(query)],
      [equals('command'), () => this.searchCommandHintQuery(query)],
      [equals('commandMention'), () => this.searchCommandMentionHintQuery(query)],
      [equals('commandChannelMention'), () => this.searchCommandChMentionHintQuery(query)],
    ])(type);
  };

  public handleHintAndQuery = debounce(({ hintName, query }: { hintName: string; query: string }) => {
    this.handleQuery(query, hintName);
  }, 200);

  private setDefaultChannelMentionItems = () => {
    this.setItems(this.defaultChannelMentionItems);
  };

  private setFetchingItems = (value: boolean) => {
    editorHintStore.setFetchingItems(value);
  };

  private setItems = (items: Member[]) => {
    editorHintStore.setItems(items);
  };

  private searchAndSetMentionItems = (query: string) => {
    wrapLoading(
      () =>
        this.actions.searchActions
          .searchMembers({ keyword: query, boostMembersId: this.mainContentsChannel?.members ?? [] })
          .then((members) => this.concatDefaultMentionItems(query, members))
          .then(this.setItems, logging.error),
      this.setFetchingItems
    );
  };
}

export default HintActions;
